/** Top-banner for TimesheetDetailPage: attention budget + navigation.
 *
 * Shows two parallel signals — confidence (cellStatus) and plausibility
 * (value / sequence / symbol-exclusivity). Each cell can need attention
 * for either reason; the counter sums them.
 *
 * Birgit-flow controls (shared across Übersicht + Vergleichen views — no
 * drift): jump-first / prev / "OK so" / next. Same code, same behaviour,
 * regardless of which view is active.
 */
import { STATUS_ICONS, STATUS_LABELS } from "../lib/cellStatus";
import type { CellStatus } from "../lib/cellStatus";
import { attentionBannerVariant } from "../lib/attentionBanner";

interface Props {
  breakdown: Record<CellStatus, number>;
  /** Plausibility counts (red = atypical/sequence/symbol-exclusive conflict, yellow = 5-min raster). */
  plausCounts?: { red: number; yellow: number };
  /** Total cells in attention pool (confidence + plausibility - accepted). */
  attentionItemsCount?: number;
  /** State-machine state of the card. */
  cardState?: string;
  /** Jump to first attention item (overview state). */
  onJump?: () => void;
  /** Step back/forward through attention items. */
  onPrev?: () => void;
  onNext?: () => void;
  /** Mark currently-active cell as "OK so" (accepted). */
  onAcceptCurrent?: () => void;
  /** Is there an active cell to accept? */
  hasActive?: boolean;
}

export function AttentionCounter({
  breakdown, plausCounts, attentionItemsCount, cardState,
  onJump, onPrev, onNext, onAcceptCurrent, hasActive,
}: Props) {
  // Use the aggregate count if provided, else fall back to confidence-only
  const need = attentionItemsCount ?? (breakdown.korrigieren + breakdown.pruefen);
  const variant = attentionBannerVariant(cardState, need);

  if (variant.kind === "ocr_in_flight") {
    return (
      <div className="attention-counter attention-pending" role="status" aria-live="polite">
        <span className="ac-icon" aria-hidden="true">⏳</span>
        <span className="ac-text">{variant.label}.</span>
      </div>
    );
  }

  if (variant.kind === "all_done") {
    return (
      <div className="attention-counter attention-done" role="status">
        <span className="ac-icon">✓</span>
        <span className="ac-text">
          Alle Auffälligkeiten geprüft oder bestätigt — Speichern + RZL-Export sind frei.
        </span>
      </div>
    );
  }

  const detailParts: string[] = [];
  if (breakdown.korrigieren > 0) detailParts.push(`${breakdown.korrigieren}× ${STATUS_LABELS.korrigieren}`);
  if (breakdown.pruefen > 0) detailParts.push(`${breakdown.pruefen}× ${STATUS_LABELS.pruefen}`);
  if (plausCounts && plausCounts.red > 0) detailParts.push(`${plausCounts.red}× Plausibilität rot`);
  if (plausCounts && plausCounts.yellow > 0) detailParts.push(`${plausCounts.yellow}× Plausibilität gelb`);

  // Severity: rot wenn echte Blocker (korrigieren ODER Plausibilität rot),
  // gelb wenn nur Warnungen (prüfen / Plausibilität gelb). Gelb blockt
  // weder Save noch Validate — Banner-Farbe spiegelt das wider (Yellow-Ton)
  // damit User es nicht als Fehler interpretiert.
  const hasRed = breakdown.korrigieren > 0 || (plausCounts && plausCounts.red > 0);
  const variantClass = hasRed ? "attention-pending" : "attention-warn";
  const icon = hasRed ? STATUS_ICONS.korrigieren : STATUS_ICONS.pruefen;

  return (
    <div className={`attention-counter ${variantClass}`} role="status">
      <span className="ac-icon" aria-hidden="true">{icon}</span>
      <span className="ac-text">
        <strong>{need}</strong> {need === 1 ? "Auffälligkeit braucht" : "Auffälligkeiten brauchen"} Aufmerksamkeit
        {detailParts.length > 0 && (
          <span className="ac-detail"> — {detailParts.join(", ")}</span>
        )}
      </span>
      <div className="ac-controls">
        {onJump && need > 0 && (
          <button type="button" className="ac-jump" onClick={onJump}
                  title="Zur ersten offenen Auffälligkeit">
            erste →
          </button>
        )}
        {onPrev && (
          <button type="button" className="ac-nav" onClick={onPrev}
                  title="Vorherige Auffälligkeit"
                  disabled={need === 0}>← vorherige</button>
        )}
        {onAcceptCurrent && (
          <button type="button" className="ac-accept" onClick={onAcceptCurrent}
                  title="Aktuelle Zelle als ‚geprüft, bleibt wie sie ist‘ markieren"
                  disabled={!hasActive}>✓ OK so</button>
        )}
        {onNext && (
          <button type="button" className="ac-nav" onClick={onNext}
                  title="Nächste Auffälligkeit"
                  disabled={need === 0}>nächste →</button>
        )}
      </div>
    </div>
  );
}
